import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL || '';

// ---- Formatters (ported from the source HTML's fmtINR/fmtNum) ----
const fmtINR = (n, compact = false) => {
  if (n === null || n === undefined || isNaN(n)) return '—';
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  if (compact) {
    if (abs >= 1e7) return sign + '₹' + (abs / 1e7).toFixed(2) + ' Cr';
    if (abs >= 1e5) return sign + '₹' + (abs / 1e5).toFixed(2) + ' L';
    if (abs >= 1e3) return sign + '₹' + (abs / 1e3).toFixed(1) + 'K';
    return sign + '₹' + abs.toFixed(0);
  }
  return sign + '₹' + abs.toLocaleString('en-IN', { maximumFractionDigits: 2 });
};
const fmtNum = (n) => {
  if (n === null || n === undefined || isNaN(n)) return '—';
  return n.toLocaleString('en-IN', { maximumFractionDigits: 2 });
};
const shortDate = (generated) => {
  if (!generated) return '';
  const p = generated.split(' ');
  return p[0] + ' ' + p[1];
};

// ---- Nice-tick math for chart axes ----
const niceStep = (raw) => {
  if (raw <= 0) return 1;
  const exp = Math.floor(Math.log10(raw));
  const base = Math.pow(10, exp);
  const f = raw / base;
  const nf = f <= 1 ? 1 : f <= 2 ? 2 : f <= 5 ? 5 : 10;
  return nf * base;
};
const makeTicks = (max, count) => {
  if (max <= 0) return [0, 1];
  const step = niceStep(max / count);
  const niceMax = Math.ceil(max / step) * step;
  const ticks = [];
  for (let v = 0; v <= niceMax + step * 0.001; v += step) ticks.push(Math.round(v * 100) / 100);
  return ticks;
};
// Rounded-top / square-bottom bar (4px radius) — spec: "rounded data-end, square at baseline"
const roundedTopRectPath = (x, yTop, w, h, r) => {
  r = Math.min(r, w / 2, h);
  if (h <= 0) return '';
  return `M${x},${yTop + r} Q${x},${yTop} ${x + r},${yTop} H${x + w - r} Q${x + w},${yTop} ${x + w},${yTop + r} V${yTop + h} H${x} Z`;
};

// Merge one or more consecutive daily registers into a single period view.
// Opening = snapshot as-on the "from" date, Closing = snapshot as-on the "to" date.
// Receipt & Issue are flows, so summed across every day in the range.
const computeData = (periods, fromId, toId) => {
  if (fromId > toId) [fromId, toId] = [toId, fromId];
  const periodIds = Object.keys(periods).sort();
  const range = periodIds.filter((id) => id >= fromId && id <= toId);
  const eff = range.length ? range : [toId];
  const startId = eff[0], endId = eff[eff.length - 1];
  const startItems = new Map(periods[startId].items.map((it) => [it.c, it]));
  const endItems = new Map(periods[endId].items.map((it) => [it.c, it]));
  const merged = new Map();
  const get = (code) => {
    let m = merged.get(code);
    if (!m) { m = { c: code, n: '', u: '', cat: '', oq: 0, ov: 0, rq: 0, rv: 0, iq: 0, iv: 0, cq: 0, cv: 0, ad: null }; merged.set(code, m); }
    return m;
  };
  eff.forEach((id) => {
    periods[id].items.forEach((it) => {
      const m = get(it.c);
      m.n = it.n; m.u = it.u || m.u; m.cat = it.cat || m.cat;
      m.rq += it.rq; m.rv += it.rv;
      m.iq += it.iq; m.iv += it.iv;
    });
  });
  startItems.forEach((it, code) => { const m = get(code); m.oq = it.oq; m.ov = it.ov; m.n = m.n || it.n; m.u = m.u || it.u; m.cat = m.cat || it.cat || ''; });
  endItems.forEach((it, code) => { const m = get(code); m.cq = it.cq; m.cv = it.cv; m.ad = it.ad; m.n = it.n; m.u = it.u || m.u; m.cat = it.cat || m.cat || ''; });
  const items = [...merged.values()];
  return {
    items,
    total_items: endItems.size,
    closing_total: items.reduce((s, it) => s + it.cv, 0),
    receipt_total: items.reduce((s, it) => s + it.rv, 0),
    issue_total: items.reduce((s, it) => s + it.iv, 0),
    opening_total: items.reduce((s, it) => s + it.ov, 0),
    aging: periods[endId].aging || [],
    isRange: eff.length > 1,
    fromGenerated: periods[startId].generated,
    toGenerated: periods[endId].generated,
  };
};

// ---- Chart components ----
const CLOSING_W = 520, CLOSING_H = 220, PAD_L = 54, PAD_R = 16, PAD_T = 16, PAD_B = 30;
const PLOT_W = CLOSING_W - PAD_L - PAD_R;
const PLOT_H = CLOSING_H - PAD_T - PAD_B;

const ClosingChart = ({ totals, onHover }) => {
  const maxVal = Math.max(...totals.map((d) => d.closing), 0);
  const ticks = makeTicks(maxVal, 4);
  const niceMax = ticks[ticks.length - 1] || 1;
  const n = totals.length;
  const xAt = (i) => PAD_L + (n === 1 ? PLOT_W / 2 : (PLOT_W * i) / (n - 1));
  const yAt = (v) => PAD_T + PLOT_H - (v / niceMax) * PLOT_H;
  const linePts = totals.map((d, i) => `${xAt(i)},${yAt(d.closing)}`).join(' L');
  const [hoverIdx, setHoverIdx] = useState(-1);
  return (
    <svg viewBox={`0 0 ${CLOSING_W} ${CLOSING_H}`} role="img" aria-label="Daily closing stock value" style={{ width: '100%', height: 'auto' }}>
      {ticks.map((t) => (
        <g key={t}>
          <line className="chart-axis-line" x1={PAD_L} x2={CLOSING_W - PAD_R} y1={yAt(t)} y2={yAt(t)} />
          <text className="chart-axis-text" x={PAD_L - 8} y={yAt(t) + 3} textAnchor="end">{fmtINR(t, true)}</text>
        </g>
      ))}
      {totals.map((d, i) => (
        <text key={`x-${i}`} className="chart-axis-text" x={xAt(i)} y={CLOSING_H - 8} textAnchor="middle">{shortDate(d.generated)}</text>
      ))}
      <path d={`M${linePts}`} fill="none" stroke="var(--series-1)" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
      {hoverIdx >= 0 && <line className="chart-crosshair" x1={xAt(hoverIdx)} x2={xAt(hoverIdx)} y1={PAD_T} y2={PAD_T + PLOT_H} style={{ opacity: 1 }} />}
      {totals.map((d, i) => (
        <g key={`m-${i}`}>
          <circle cx={xAt(i)} cy={yAt(d.closing)} r={hoverIdx === i ? 7 : 5} fill="var(--series-1)" stroke="var(--surface-1)" strokeWidth="2" />
          {i === n - 1 && <text className="chart-endpoint-label" x={xAt(i)} y={yAt(d.closing) - 12} textAnchor="end">{fmtINR(d.closing, true)}</text>}
          <circle className="chart-hit" cx={xAt(i)} cy={yAt(d.closing)} r="16"
            onPointerMove={(e) => { setHoverIdx(i); onHover({ x: e.clientX, y: e.clientY, html: `<strong>${d.generated}</strong><br/>Closing stock: <strong>${fmtINR(d.closing, false)}</strong>` }); }}
            onPointerLeave={() => { setHoverIdx(-1); onHover(null); }} />
        </g>
      ))}
    </svg>
  );
};

const FlowChart = ({ totals, onHover }) => {
  const maxVal = Math.max(...totals.map((d) => Math.max(d.receipt, d.issue)), 0);
  const ticks = makeTicks(maxVal, 4);
  const niceMax = ticks[ticks.length - 1] || 1;
  const n = totals.length;
  const groupW = PLOT_W / n;
  const barW = Math.min(24, groupW * 0.32);
  const gap = 2;
  const yAt = (v) => PAD_T + PLOT_H - (v / niceMax) * PLOT_H;
  return (
    <svg viewBox={`0 0 ${CLOSING_W} ${CLOSING_H}`} role="img" aria-label="Daily receipts vs issues" style={{ width: '100%', height: 'auto' }}>
      {ticks.map((t) => (
        <g key={t}>
          <line className="chart-axis-line" x1={PAD_L} x2={CLOSING_W - PAD_R} y1={yAt(t)} y2={yAt(t)} />
          <text className="chart-axis-text" x={PAD_L - 8} y={yAt(t) + 3} textAnchor="end">{fmtINR(t, true)}</text>
        </g>
      ))}
      {totals.map((d, i) => {
        const groupCx = PAD_L + groupW * (i + 0.5);
        return (
          <g key={`bar-${i}`}>
            <text className="chart-axis-text" x={groupCx} y={CLOSING_H - 8} textAnchor="middle">{shortDate(d.generated)}</text>
            {[
              { key: 'receipt', color: 'var(--series-1)', dx: -gap / 2 - barW, label: 'Receipt' },
              { key: 'issue', color: 'var(--series-2)', dx: gap / 2, label: 'Issue' },
            ].map((spec) => {
              const val = d[spec.key];
              const barH = PLOT_H - (yAt(val) - PAD_T);
              const x = groupCx + spec.dx;
              const yTop = yAt(val);
              return (
                <g key={spec.key}>
                  <path d={roundedTopRectPath(x, yTop, barW, barH, 4)} fill={spec.color} />
                  <rect className="chart-hit" x={x} y={PAD_T} width={barW} height={PLOT_H}
                    onPointerMove={(e) => onHover({ x: e.clientX, y: e.clientY, html: `<strong>${d.generated}</strong><br/>${spec.label}: <strong>${fmtINR(val, false)}</strong>` })}
                    onPointerLeave={() => onHover(null)} />
                </g>
              );
            })}
          </g>
        );
      })}
    </svg>
  );
};

// ---- Sortable table helpers ----
const SortArrow = ({ active, dir }) => active ? <span className="sort-arrow">{dir === 1 ? '▲' : '▼'}</span> : null;

// ---- Main component ----
const KaplStorePage = () => {
  const { user } = useAuth();
  // Match the Procurement pattern: dedicated upload page owns uploads for the
  // stores role. Only admin sees the inline upload button on the dashboard,
  // so the dashboard stays a pure view for stores users (same as Price Trend
  // for procurement).
  const canUpload = false;  // Culture Store has no Excel upload flow yet — manual entry only
  const [periods, setPeriods] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [fromId, setFromId] = useState(null);
  const [toId, setToId] = useState(null);
  const [dailyRange, setDailyRange] = useState('7');
  const [dailySort, setDailySort] = useState({ key: 'id', dir: 1 });
  const [itemSort, setItemSort] = useState({ key: 'c', dir: 1 });
  const [issueSort, setIssueSort] = useState({ key: 'v', dir: -1 });
  const [receiptSort, setReceiptSort] = useState({ key: 'v', dir: -1 });
  const [searchQ, setSearchQ] = useState('');
  const [negOnly, setNegOnly] = useState(false);
  const [activeFlowTab, setActiveFlowTab] = useState('none'); // 'none' | 'issues' | 'receipts'
  const [agingExpanded, setAgingExpanded] = useState(new Set());
  const [tooltip, setTooltip] = useState(null);
  const itemDetailRef = useRef(null);

  // Upload panel state (admin + stores role only)
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploadMsg, setUploadMsg] = useState('');
  const [dataVersion, setDataVersion] = useState(0);
  const fileInputRef = useRef(null);

  const loadFiles = useCallback(async () => {
    if (!canUpload) return;
    try {
      const r = await fetch(`${API}/api/general-stores/files`, { credentials: 'include' });
      if (r.ok) setUploadedFiles((await r.json()).dates || []);
    } catch {}
  }, [canUpload]);

  useEffect(() => { if (uploadOpen) loadFiles(); }, [uploadOpen, loadFiles]);

  const handleFilePick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploadBusy(true); setUploadMsg('');
    let ok = 0, fail = 0;
    for (const file of files) {
      const fd = new FormData(); fd.append('file', file);
      try {
        const r = await fetch(`${API}/api/general-stores/upload`, { method: 'POST', body: fd, credentials: 'include' });
        const j = await r.json();
        if (r.ok && j.ok) { ok++; setUploadMsg(`${file.name} → ${j.date} (${j.n_items} items${j.replaced_existing ? ', replaced' : ''})`); }
        else { fail++; setUploadMsg(`${file.name}: ${j.detail || 'failed'}`); }
      } catch (err) { fail++; setUploadMsg(`${file.name}: ${err.message}`); }
    }
    setUploadBusy(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setUploadMsg(prev => `${ok} uploaded, ${fail} failed. ${prev}`.trim());
    await loadFiles();
    setDataVersion(v => v + 1);  // triggers dataset re-fetch below
  };

  const handleDelete = async (date) => {
    if (!window.confirm(`Remove ${date} and rebuild?`)) return;
    setUploadBusy(true);
    try {
      const r = await fetch(`${API}/api/general-stores/files/${date}`, { method: 'DELETE', credentials: 'include' });
      const j = await r.json();
      setUploadMsg(r.ok ? `Removed ${date}` : (j.detail || 'delete failed'));
    } catch (e) { setUploadMsg(e.message); }
    setUploadBusy(false);
    await loadFiles();
    setDataVersion(v => v + 1);
  };

  // Load data
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/kapl/data`, { cache: 'no-store' });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();        if (cancelled) return;
        setPeriods(j);
        const ids = Object.keys(j).sort();
        setFromId(ids[ids.length - 1]);
        setToId(ids[ids.length - 1]);
      } catch (e) {
        if (!cancelled) setErr(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [dataVersion]);

  const periodIds = useMemo(() => (periods ? Object.keys(periods).sort() : []), [periods]);
  const DATA = useMemo(() => (periods && fromId && toId ? computeData(periods, fromId, toId) : null), [periods, fromId, toId]);

  // Category (aka Department in the Culture register) breakdown for the
  // currently-selected period. Bars scale to the max amount, and rows sort
  // by closing value desc — matches the reference HTML this dashboard was
  // built from. Categories with zero closing value are dropped so the card
  // doesn't show noise for a period where nothing in that group is on hand.
  const [catSort, setCatSort] = useState({ key: 'amount', dir: -1 });
  const catBreakdown = useMemo(() => {
    if (!DATA) return { rows: [], grandTotal: 0 };
    const acc = new Map();
    DATA.items.forEach((it) => {
      const k = (it.cat || 'Uncategorised');
      const prev = acc.get(k) || { dept: k, items: 0, amount: 0 };
      prev.items += 1;
      prev.amount += it.cv || 0;
      acc.set(k, prev);
    });
    const grandTotal = [...acc.values()].reduce((s, r) => s + r.amount, 0);
    const rows = [...acc.values()]
      .filter((r) => Math.abs(r.amount) > 0.005 || r.items > 0)
      .map((r) => ({ ...r, pct: grandTotal ? (r.amount / grandTotal * 100) : 0 }));
    const cmp = (a, b) => {
      const va = a[catSort.key], vb = b[catSort.key];
      if (typeof va === 'string') return (va || '').localeCompare(vb || '') * catSort.dir;
      return ((va || 0) - (vb || 0)) * catSort.dir;
    };
    rows.sort(cmp);
    return { rows, grandTotal };
  }, [DATA, catSort]);
  const toggleCatSort = (key, isNum) => setCatSort(catSort.key === key ? { key, dir: -catSort.dir } : { key, dir: isNum ? -1 : 1 });

  const dailyTotals = useMemo(() => {
    if (!periods) return [];
    return periodIds.map((id) => {
      let closing = 0, receipt = 0, issue = 0;
      periods[id].items.forEach((it) => { closing += it.cv; receipt += it.rv; issue += it.iv; });
      return { id, generated: periods[id].generated, closing, receipt, issue };
    });
  }, [periods, periodIds]);

  const visibleDailyTotals = useMemo(() => {
    if (dailyRange === 'all') return dailyTotals;
    const n = parseInt(dailyRange, 10) || 7;
    return dailyTotals.slice(-n);
  }, [dailyTotals, dailyRange]);

  const sortedDaily = useMemo(() => [...visibleDailyTotals].sort((a, b) => {
    const va = a[dailySort.key], vb = b[dailySort.key];
    if (typeof va === 'string') return va.localeCompare(vb) * dailySort.dir;
    return (va - vb) * dailySort.dir;
  }), [visibleDailyTotals, dailySort]);

  const issueRows = useMemo(() => {
    if (!DATA) return [];
    const total = DATA.issue_total || 0;
    const arr = DATA.items.filter((it) => it.iv > 0).map((it) => ({ n: it.n, q: it.iq, v: it.iv, pct: total ? (it.iv / total * 100) : 0 }));
    arr.sort((a, b) => {
      const va = a[issueSort.key], vb = b[issueSort.key];
      if (typeof va === 'string') return va.localeCompare(vb) * issueSort.dir;
      return (va - vb) * issueSort.dir;
    });
    return arr;
  }, [DATA, issueSort]);

  const receiptRows = useMemo(() => {
    if (!DATA) return [];
    const total = DATA.receipt_total || 0;
    const arr = DATA.items.filter((it) => it.rv > 0).map((it) => ({ n: it.n, q: it.rq, v: it.rv, pct: total ? (it.rv / total * 100) : 0 }));
    arr.sort((a, b) => {
      const va = a[receiptSort.key], vb = b[receiptSort.key];
      if (typeof va === 'string') return va.localeCompare(vb) * receiptSort.dir;
      return (va - vb) * receiptSort.dir;
    });
    return arr;
  }, [DATA, receiptSort]);

  const filteredItems = useMemo(() => {
    if (!DATA) return [];
    let rows = DATA.items;
    const q = searchQ.trim().toLowerCase();
    if (q) rows = rows.filter((it) => (it.n || '').toLowerCase().includes(q) || (it.c || '').toLowerCase().includes(q));
    if (negOnly) rows = rows.filter((it) => it.cq < -1e-6 || it.cv < -0.005);
    return [...rows].sort((a, b) => {
      const va = a[itemSort.key], vb = b[itemSort.key];
      if (typeof va === 'string') return ((va || '').localeCompare(vb || '')) * itemSort.dir;
      return ((va || 0) - (vb || 0)) * itemSort.dir;
    });
  }, [DATA, searchQ, negOnly, itemSort]);

  const negCount = useMemo(() => DATA ? DATA.items.filter((it) => it.cq < -1e-6 || it.cv < -0.005).length : 0, [DATA]);

  const onHover = useCallback((info) => setTooltip(info), []);

  const jumpToNegativeStock = () => {
    setActiveFlowTab('none');
    setSearchQ('');
    setNegOnly(true);
    setTimeout(() => itemDetailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };

  const toggleSort = (state, setState, key, isNum) => {
    if (state.key === key) setState({ key, dir: -state.dir });
    else setState({ key, dir: isNum ? -1 : 1 });
  };

  if (loading) return <div className="gs-root"><style>{DASHBOARD_CSS}</style><div style={{ padding: 40, color: 'var(--text-muted)' }}>Loading General Stores data…</div></div>;
  if (err) return <div className="gs-root"><style>{DASHBOARD_CSS}</style><div style={{ padding: 40, color: 'var(--critical-text)' }}>Failed to load: {err}</div></div>;
  if (!DATA) return null;

  return (
    <div className="gs-root" data-testid="kapl-store-page">
      <style>{DASHBOARD_CSS}</style>
      <div className="viz-root">
        <div className="wrap">
          {/* Header */}
          <div className="header">
            <div>
              <h1>KAPL — Feed Store Closing Stock</h1>
              <div className="loc">General Stores (All Locations) — as on {DATA.toGenerated}</div>
            </div>
            {canUpload && (
              <button type="button" className="upload-btn" onClick={() => setUploadOpen(v => !v)} data-testid="gs-upload-toggle">
                {uploadOpen ? 'Close' : '↑ Upload Data'}
              </button>
            )}
          </div>

          {/* Upload panel (admin + stores only) */}
          {canUpload && uploadOpen && (
            <div className="upload-panel" data-testid="gs-upload-panel">
              <div className="upload-panel-title">Upload daily MRO Stock Summary (.xlsx)</div>
              <p className="upload-panel-sub">
                Drop one or more MRO Stock Summary Excel files. The "To" date is read from row 6 inside
                each file (filenames are ignored). Re-uploading the same date replaces it and rebuilds the FIFO dataset.
              </p>
              <div className="upload-controls">
                <input ref={fileInputRef} type="file" multiple accept=".xlsx,.xls"
                  onChange={handleFilePick} disabled={uploadBusy} data-testid="gs-file-input" />
                {uploadBusy && <span className="upload-status">Working…</span>}
                {uploadMsg && !uploadBusy && <span className="upload-status">{uploadMsg}</span>}
              </div>
              <div className="upload-file-list">
                <div className="upload-file-list-title">Uploaded ({uploadedFiles.length})</div>
                {uploadedFiles.length === 0
                  ? <div className="upload-empty">No files uploaded yet.</div>
                  : (
                    <table className="upload-file-table">
                      <thead><tr><th>Date</th><th>Original filename</th><th className="num">Items</th><th>Uploaded</th><th></th></tr></thead>
                      <tbody>
                        {uploadedFiles.map((f) => (
                          <tr key={f.date}>
                            <td>{f.date}</td>
                            <td className="name-cell">{f.original_filename || '—'}</td>
                            <td className="num">{f.n_items || '—'}</td>
                            <td>{f.uploaded_at ? new Date(f.uploaded_at).toLocaleDateString() : '—'}</td>
                            <td><button className="upload-del-btn" onClick={() => handleDelete(f.date)} disabled={uploadBusy}>Remove</button></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )
                }
              </div>
            </div>
          )}

          {/* Period selector */}
          <div className="filter-row period-filter-row">
            <label className="period-label">Period</label>
            <select value={fromId} onChange={(e) => setFromId(e.target.value)} data-testid="gs-period-from">
              {periodIds.map((id) => <option key={id} value={id}>{periods[id].generated}</option>)}
            </select>
            <span className="period-to-label">to</span>
            <select value={toId} onChange={(e) => { const v = e.target.value; setToId(v); if (fromId > v) setFromId(v); }} data-testid="gs-period-to">
              {periodIds.map((id) => <option key={id} value={id}>{periods[id].generated}</option>)}
            </select>
            <span className="period-note">
              {DATA.isRange
                ? `Opening as on ${DATA.fromGenerated} · Closing as on ${DATA.toGenerated} · Receipts & Issues totalled ${DATA.fromGenerated} – ${DATA.toGenerated}`
                : `Showing ${DATA.toGenerated}`}
            </span>
          </div>

          <p className="card-sub" style={{ margin: '0 0 18px' }}>
            Closing &amp; Issue values below use <strong>FIFO costing</strong>: each day's receipts are layered at their own per-unit cost (Receipt Value ÷ Receipt Qty), and issues consume the oldest layer first. Quantities and Receipt values match the source export exactly; Closing/Issue figures differ from the export's own moving-average valuation by design.
          </p>

          {/* KPI row */}
          <div className="kpi-row">
            <div className={`kpi hero kpi-clickable${activeFlowTab === 'none' ? ' active' : ''}`} role="button" tabIndex={0}
              onClick={() => setActiveFlowTab('none')}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setActiveFlowTab('none'); } }}>
              <div className="label">Total closing stock value (FIFO)</div>
              <div className="value">{fmtINR(DATA.closing_total, true)}</div>
              <div className="sub">{fmtINR(DATA.closing_total, false)}</div>
            </div>
            <div className="kpi">
              <div className="label">Line items</div>
              <div className="value">{fmtNum(DATA.total_items)}</div>
              <div className="sub">as on {DATA.toGenerated}</div>
              {negCount > 0 && (
                <button type="button" className="neg-flag" onClick={jumpToNegativeStock}
                  aria-label={`${negCount} items have negative closing stock — jump to Item-level Detail, filtered`}>
                  ⚠ {negCount} with negative closing stock
                </button>
              )}
            </div>
            <div className={`kpi kpi-clickable${activeFlowTab === 'receipts' ? ' active' : ''}`} role="button" tabIndex={0}
              onClick={() => setActiveFlowTab('receipts')}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setActiveFlowTab('receipts'); } }}>
              <div className="label">Receipts this period</div>
              <div className="value">{fmtINR(DATA.receipt_total, true)}</div>
              <div className="sub">{fmtINR(DATA.receipt_total, false)}</div>
            </div>
            <div className={`kpi kpi-clickable${activeFlowTab === 'issues' ? ' active' : ''}`} role="button" tabIndex={0}
              onClick={() => setActiveFlowTab('issues')}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setActiveFlowTab('issues'); } }}>
              <div className="label">Issues this period</div>
              <div className="value">{fmtINR(DATA.issue_total, true)}</div>
              <div className="sub">{fmtINR(DATA.issue_total, false)}</div>
            </div>
          </div>

          {/* Daily Trend */}
          {activeFlowTab === 'none' && (
            <div className="card overview-card">
              <p className="card-title">Daily Trend</p>
              <p className="card-sub">Closing stock (FIFO), receipts and issues (FIFO) · independent of the period filter above</p>
              <div className="filter-row" style={{ marginBottom: 16 }}>
                <select value={dailyRange} onChange={(e) => setDailyRange(e.target.value)} data-testid="gs-daily-range">
                  <option value="7">Last 7 days</option>
                  <option value="all">All time</option>
                </select>
              </div>
              <div className="daily-trend-charts">
                <div>
                  <div className="chart-label">Closing Stock Value</div>
                  <div className="chart-svg-wrap"><ClosingChart totals={visibleDailyTotals} onHover={onHover} /></div>
                </div>
                <div>
                  <div className="chart-label">Receipts vs Issues</div>
                  <div className="chart-legend">
                    <div className="chart-legend-item"><span className="chart-legend-swatch" style={{ background: 'var(--series-1)' }} /><span>Receipt</span></div>
                    <div className="chart-legend-item"><span className="chart-legend-swatch" style={{ background: 'var(--series-2)' }} /><span>Issue</span></div>
                  </div>
                  <div className="chart-svg-wrap"><FlowChart totals={visibleDailyTotals} onHover={onHover} /></div>
                </div>
              </div>

              <div className="dept-table-wrap">
                <table>
                  <thead>
                    <tr>
                      {[['id', 'Date', false], ['closing', 'Closing Stock Value (₹, FIFO)', true], ['receipt', 'Receipt Value (₹)', true], ['issue', 'Issue Value (₹, FIFO)', true]].map(([k, lbl, num]) => (
                        <th key={k} className={num ? 'num' : ''} style={{ cursor: 'pointer' }} onClick={() => toggleSort(dailySort, setDailySort, k, num)}>
                          {lbl}<SortArrow active={dailySort.key === k} dir={dailySort.dir} />
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedDaily.map((d) => (
                      <tr key={d.id}>
                        <td>{d.generated}</td>
                        <td className="num">{fmtINR(d.closing, false)}</td>
                        <td className="num">{fmtINR(d.receipt, false)}</td>
                        <td className="num">{fmtINR(d.issue, false)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Stock Aging */}
          {activeFlowTab === 'none' && (
            <div className="card overview-card">
              <p className="card-title">Stock Aging</p>
              <p className="card-sub">Closing stock as on {DATA.toGenerated}, aged by FIFO receipt layer · click a row to see the items in that age band</p>
              <div className="dept-table-wrap">
                <table>
                  <thead>
                    <tr><th>Age</th><th className="num">Qty</th><th className="amount-th">Value (₹)</th><th className="num">% of Total</th></tr>
                  </thead>
                  <tbody>
                    {(DATA.aging || []).map((b) => {
                      const maxV = Math.max(...(DATA.aging || []).map((x) => x.value), 0);
                      const widthPct = maxV ? (b.value / maxV * 100) : 0;
                      const isOpen = agingExpanded.has(b.label);
                      return (
                        <React.Fragment key={b.label}>
                          <tr className={`expandable-row${isOpen ? ' expanded' : ''}`} role="button" tabIndex={0} aria-expanded={isOpen}
                            onClick={() => {
                              const next = new Set(agingExpanded);
                              if (next.has(b.label)) next.delete(b.label); else next.add(b.label);
                              setAgingExpanded(next);
                            }}>
                            <td className="name-cell"><span className="chevron">{isOpen ? '▾' : '▸'}</span><span>{b.label}</span></td>
                            <td className="num">{fmtNum(b.qty)}</td>
                            <td className="row-bar-cell">
                              <div className="row-bar-wrap">
                                <div className="row-bar-track"><div className="row-bar-fill receipt" style={{ width: widthPct + '%' }} /></div>
                                <span className="row-bar-value">{fmtINR(b.value, false)}</span>
                              </div>
                            </td>
                            <td className="num">{b.pct.toFixed(1)}%</td>
                          </tr>
                          {isOpen && (
                            <tr className="items-subrow"><td colSpan={4}>
                              {(b.items || []).length === 0
                                ? <div className="items-empty">No stock currently in this age band.</div>
                                : (
                                  <div className="items-subtable-wrap"><table className="items-subtable">
                                    <thead><tr><th>Item Code</th><th>Item Name</th><th>Received</th><th className="num">Age (days)</th><th className="num">Qty</th><th className="num">Value (₹)</th></tr></thead>
                                    <tbody>
                                      {b.items.map((it, i) => (
                                        <tr key={i}>
                                          <td>{(it.c || '').startsWith('NOCODE::') ? '—' : it.c}</td>
                                          <td className="name-cell">{it.n}</td>
                                          <td>{it.date}</td>
                                          <td className="num">{fmtNum(it.age)}</td>
                                          <td className="num">{fmtNum(it.q)}</td>
                                          <td className="num">{fmtINR(it.v, false)}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table></div>
                                )}
                            </td></tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Issue / Receipt flow tables */}
          {activeFlowTab === 'issues' && (
            <MoverTable title="Item-wise Issues" cls="issue" rows={issueRows} sort={issueSort} setSort={setIssueSort} label="Qty Issued" valLabel="Issue Value (₹, FIFO)" />
          )}
          {activeFlowTab === 'receipts' && (
            <MoverTable title="Item-wise Receipts" cls="receipt" rows={receiptRows} sort={receiptSort} setSort={setReceiptSort} label="Qty Received" valLabel="Receipt Value (₹)" />
          )}

          {/* Item-level Detail */}
          {activeFlowTab === 'none' && (
            <div className="card overview-card" ref={itemDetailRef}>
              <p className="card-title">Item-level Detail</p>
              <p className="card-sub">Sorted by Item Code ascending by default. Opening is as-on the "from" date, Closing is as-on the "to" date; Receipt &amp; Issue are totalled across the selected period. Opening/Issue/Closing values use FIFO costing; Age is the age in days of the oldest FIFO layer still on hand · search a product, click a column header to sort, or check "Negative stock only" to isolate items that show negative Closing Qty/Value — a data-quality flag, not a valid physical stock level</p>
              <div className="filter-row">
                <input type="text" value={searchQ} onChange={(e) => setSearchQ(e.target.value)} placeholder="Search item code or name…" data-testid="gs-search" />
                <label className="neg-toggle"><input type="checkbox" checked={negOnly} onChange={(e) => setNegOnly(e.target.checked)} data-testid="gs-neg-toggle" /> Negative stock only</label>
                <span className="count">{filteredItems.length.toLocaleString('en-IN')} items</span>
              </div>
              <div className="item-table-scroll">
                <table>
                  <thead>
                    <tr>
                      {[['c', 'Item Code', false], ['n', 'Item Name', false], ['u', 'UOM', false], ['oq', 'Opening Qty', true], ['ov', 'Opening Value (₹, FIFO)', true], ['rq', 'Receipt Qty', true], ['rv', 'Receipt Value (₹)', true], ['iq', 'Issue Qty', true], ['iv', 'Issue Value (₹, FIFO)', true], ['cq', 'Closing Qty', true], ['cv', 'Closing Value (₹, FIFO)', true], ['ad', 'Age (days)', true]].map(([k, lbl, num]) => (
                        <th key={k} className={num ? 'num' : ''} style={{ cursor: 'pointer' }} onClick={() => toggleSort(itemSort, setItemSort, k, num)}>
                          {lbl}<SortArrow active={itemSort.key === k} dir={itemSort.dir} />
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredItems.map((it) => (
                      <tr key={it.c}>
                        <td>{it.c}</td>
                        <td className="name-cell">{it.n}</td>
                        <td>{it.u || '—'}</td>
                        <td className="num">{fmtNum(it.oq)}</td>
                        <td className="num">{fmtINR(it.ov, false)}</td>
                        <td className="num">{fmtNum(it.rq)}</td>
                        <td className="num">{fmtINR(it.rv, false)}</td>
                        <td className="num">{fmtNum(it.iq)}</td>
                        <td className="num">{fmtINR(it.iv, false)}</td>
                        <td className={`num${it.cq < -1e-6 ? ' negative-cell' : ''}`}>{fmtNum(it.cq)}</td>
                        <td className={`num${it.cv < -0.005 ? ' negative-cell' : ''}`}>{fmtINR(it.cv, false)}</td>
                        <td className="num">{it.ad === null || it.ad === undefined ? '—' : fmtNum(it.ad)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <footer>Source: KAPL Feed Store daily closing stock register · Quantities and Receipt values reconciled exactly against each day's own register · Opening/Issue/Closing values recomputed on a FIFO basis (receipts layered at their own per-unit cost, oldest layer issued first) and will not match the register's own moving-average figures by design</footer>
        </div>
      </div>

      {tooltip && (
        <div className="tooltip" style={{ opacity: 1, left: tooltip.x + 14, top: tooltip.y + 14 }} dangerouslySetInnerHTML={{ __html: tooltip.html }} />
      )}
    </div>
  );
};

const MoverTable = ({ title, cls, rows, sort, setSort, label, valLabel }) => {
  const maxV = Math.max(...rows.map((r) => r.v), 0);
  const toggle = (key, isNum) => sort.key === key ? setSort({ key, dir: -sort.dir }) : setSort({ key, dir: isNum ? -1 : 1 });
  return (
    <div className="card">
      <p className="card-title">{title}</p>
      <p className="card-sub">Material {cls === 'issue' ? 'issued' : 'received'} this period{cls === 'issue' ? ', valued on a FIFO basis' : ''} · click a column header to sort · click "Total closing stock value" above to go back to the overview</p>
      <div className="item-table-scroll">
        <table>
          <thead><tr>
            {[['n', 'Item', false], ['q', label, true], ['v', valLabel, true], ['pct', '% of Total', true]].map(([k, lbl, num]) => (
              <th key={k} className={num ? 'num' : (k === 'v' ? 'amount-th' : '')} style={{ cursor: 'pointer' }} onClick={() => toggle(k, num)}>
                {lbl}<SortArrow active={sort.key === k} dir={sort.dir} />
              </th>
            ))}
          </tr></thead>
          <tbody>
            {rows.map((r, i) => {
              const w = maxV ? (r.v / maxV * 100) : 0;
              return (
                <tr key={i}>
                  <td className="name-cell">{r.n}</td>
                  <td className="num">{fmtNum(r.q)}</td>
                  <td className="row-bar-cell">
                    <div className="row-bar-wrap">
                      <div className="row-bar-track"><div className={`row-bar-fill ${cls}`} style={{ width: w + '%' }} /></div>
                      <span className="row-bar-value">{fmtINR(r.v, false)}</span>
                    </div>
                  </td>
                  <td className="num">{r.pct.toFixed(1)}%</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Verbatim CSS from the source HTML, scoped under .gs-root so it doesn't leak.
const DASHBOARD_CSS = `
.gs-root {
  --surface-1:      #fcfcfb;
  --page-plane:     #f9f9f7;
  --text-primary:   #0b0b0b;
  --text-secondary: #52514e;
  --text-muted:     #898781;
  --gridline:       #e1e0d9;
  --baseline:       #c3c2b7;
  --border:         rgba(11,11,11,0.10);
  --series-1:       #2a78d6;
  --series-2:       #eb6834;
  --seq-100:        #cde2fb;
  --seq-450:        #2a78d6;
  --seq-600:        #184f95;
  --success-text:   #006300;
  --critical-text:  #d03b3b;
  font-family: system-ui, -apple-system, "Segoe UI", sans-serif;
  background: var(--page-plane);
  color: var(--text-primary);
  min-height: 100vh;
}
.gs-root * { box-sizing: border-box; }
.gs-root .viz-root { background: var(--page-plane); min-height: 100vh; padding: 28px 32px 60px; }
.gs-root .wrap { max-width: 1220px; margin: 0 auto; }
.gs-root .header { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; margin-bottom: 22px; flex-wrap: wrap; }
.gs-root .header h1 { font-size: 20px; font-weight: 650; margin: 0 0 4px; letter-spacing: -0.01em; }
.gs-root .header .loc { font-size: 12.5px; color: var(--text-muted); max-width: 640px; line-height: 1.5; }

.gs-root .kpi-row { display: grid; grid-template-columns: 1.4fr repeat(3, 1fr); gap: 12px; margin-bottom: 20px; }
.gs-root .kpi { background: var(--surface-1); border: 1px solid var(--border); border-radius: 10px; padding: 16px 18px; }
.gs-root .kpi .label { font-size: 12px; color: var(--text-secondary); margin-bottom: 8px; }
.gs-root .kpi .value { font-size: 26px; font-weight: 650; letter-spacing: -0.01em; }
.gs-root .kpi.hero .value { font-size: 32px; color: var(--series-1); }
.gs-root .kpi .sub { font-size: 11.5px; color: var(--text-muted); margin-top: 4px; }
.gs-root .kpi .neg-flag { display: inline-flex; align-items: center; gap: 4px; margin-top: 6px; font-size: 11.5px; font-weight: 650; color: var(--critical-text); cursor: pointer; background: none; border: none; padding: 0; font-family: inherit; }
.gs-root .kpi .neg-flag:hover, .gs-root .kpi .neg-flag:focus-visible { text-decoration: underline; outline: none; }
.gs-root .kpi-clickable { cursor: pointer; transition: box-shadow .12s, border-color .12s; }
.gs-root .kpi-clickable:hover, .gs-root .kpi-clickable:focus-visible { box-shadow: inset 0 0 0 1px var(--series-1); border-color: var(--series-1); outline: none; }
.gs-root .kpi-clickable.active { box-shadow: inset 0 0 0 2px var(--series-1); border-color: var(--series-1); }
.gs-root .kpi-clickable.active .label, .gs-root .kpi-clickable.active .value { color: var(--series-1); }

.gs-root .card { background: var(--surface-1); border: 1px solid var(--border); border-radius: 10px; padding: 20px 22px; margin-bottom: 20px; }
.gs-root .card-title { font-size: 15px; font-weight: 650; margin: 0 0 4px; }
.gs-root .card-sub { font-size: 12px; color: var(--text-muted); margin: 0 0 14px; line-height: 1.55; }

.gs-root .filter-row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-bottom: 12px; }
.gs-root .period-filter-row { margin-bottom: 12px; }
.gs-root .period-label { font-size: 12px; font-weight: 650; color: var(--text-secondary); }
.gs-root .period-to-label { font-size: 12px; color: var(--text-muted); }
.gs-root .period-note { font-size: 12px; color: var(--text-muted); }
.gs-root select, .gs-root input[type=text] { font-size: 12.5px; padding: 6px 10px; border: 1px solid var(--border); border-radius: 6px; background: #fff; color: var(--text-primary); font-family: inherit; }
.gs-root input[type=text] { min-width: 240px; }
.gs-root .neg-toggle { display: inline-flex; align-items: center; gap: 6px; font-size: 12px; color: var(--text-secondary); cursor: pointer; }
.gs-root .count { font-size: 11.5px; color: var(--text-muted); margin-left: auto; }

.gs-root table { width: 100%; border-collapse: collapse; }
.gs-root thead th { text-align: left; font-size: 11.5px; text-transform: uppercase; letter-spacing: 0.04em; color: var(--text-muted); font-weight: 650; padding: 8px 10px; border-bottom: 1px solid var(--border); position: relative; }
.gs-root thead th.num, .gs-root thead th.amount-th { text-align: right; }
.gs-root thead th .sort-arrow { margin-left: 4px; font-size: 10px; color: var(--series-1); }
.gs-root tbody td { padding: 8px 10px; font-size: 12.5px; border-bottom: 1px solid var(--gridline); color: var(--text-primary); font-variant-numeric: tabular-nums; }
.gs-root tbody td.num { text-align: right; }
.gs-root tbody td.name-cell { max-width: 340px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.gs-root tbody td.negative-cell { color: var(--critical-text); font-weight: 650; }
.gs-root tbody tr:hover { background: var(--page-plane); }

.gs-root .item-table-scroll, .gs-root .dept-table-wrap { overflow-x: auto; }
.gs-root .item-table-scroll table, .gs-root .dept-table-wrap table { min-width: 900px; }

.gs-root .daily-trend-charts { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 20px; }
@media (max-width: 900px) { .gs-root .daily-trend-charts { grid-template-columns: 1fr; } .gs-root .kpi-row { grid-template-columns: 1fr 1fr; } }
.gs-root .chart-label { font-size: 12px; font-weight: 650; color: var(--text-secondary); margin-bottom: 6px; }
.gs-root .chart-legend { display: flex; gap: 12px; margin-bottom: 6px; font-size: 11.5px; color: var(--text-secondary); }
.gs-root .chart-legend-item { display: inline-flex; align-items: center; gap: 5px; }
.gs-root .chart-legend-swatch { width: 10px; height: 10px; border-radius: 2px; }
.gs-root .chart-svg-wrap { background: #fff; border: 1px solid var(--border); border-radius: 8px; padding: 8px; }
.gs-root .chart-axis-line { stroke: var(--gridline); stroke-width: 1; }
.gs-root .chart-axis-text { font-size: 10px; fill: var(--text-muted); font-family: system-ui, sans-serif; }
.gs-root .chart-endpoint-label { font-size: 11px; fill: var(--series-1); font-weight: 650; font-family: system-ui, sans-serif; }
.gs-root .chart-crosshair { stroke: var(--series-1); stroke-width: 1; stroke-dasharray: 3 2; opacity: 0; pointer-events: none; }
.gs-root .chart-hit { fill: transparent; cursor: crosshair; }

.gs-root .row-bar-cell { padding: 6px 10px; }
.gs-root .row-bar-wrap { display: flex; align-items: center; gap: 10px; }
.gs-root .row-bar-track { flex: 1; height: 8px; background: var(--gridline); border-radius: 4px; overflow: hidden; }
.gs-root .row-bar-fill { height: 100%; border-radius: 4px; }
.gs-root .row-bar-fill.issue { background: var(--series-2); }
.gs-root .row-bar-fill.receipt { background: var(--series-1); }
.gs-root .row-bar-value { font-size: 12px; font-variant-numeric: tabular-nums; font-weight: 650; min-width: 92px; text-align: right; }

.gs-root .expandable-row { cursor: pointer; }
.gs-root .expandable-row:hover, .gs-root .expandable-row.expanded { background: var(--page-plane); }
.gs-root .chevron { display: inline-block; width: 12px; color: var(--text-muted); margin-right: 4px; }
.gs-root .expandable-row.expanded .chevron { color: var(--series-1); }
.gs-root .items-subrow td { padding: 0 10px 14px; background: #fbfaf5; border-bottom: 1px solid var(--gridline); }
.gs-root .items-subtable-wrap { padding: 6px 6px 2px; }
.gs-root .items-subtable { width: 100%; }
.gs-root .items-subtable thead th { font-size: 10.5px; padding: 4px 8px; }
.gs-root .items-subtable tbody td { font-size: 11.5px; padding: 4px 8px; }
.gs-root .items-empty { padding: 12px; font-size: 12px; color: var(--text-muted); font-style: italic; }

.gs-root .tooltip { position: fixed; pointer-events: none; background: #ffffff; border: 1px solid var(--border); border-radius: 6px; padding: 6px 10px; font-size: 11.5px; box-shadow: 0 4px 14px rgba(0,0,0,0.10); z-index: 999; }

.gs-root footer { text-align: center; font-size: 11px; color: var(--text-muted); margin-top: 24px; }

.gs-root .upload-btn { font-size: 12.5px; font-weight: 650; background: var(--series-1); color: #fff; border: none; border-radius: 6px; padding: 8px 14px; cursor: pointer; font-family: inherit; }
.gs-root .upload-btn:hover { background: var(--seq-600); }
.gs-root .upload-panel { background: var(--surface-1); border: 1px solid var(--border); border-left: 3px solid var(--series-1); border-radius: 10px; padding: 18px 22px; margin-bottom: 20px; }
.gs-root .upload-panel-title { font-size: 14px; font-weight: 650; margin-bottom: 4px; }
.gs-root .upload-panel-sub { font-size: 12px; color: var(--text-muted); margin: 0 0 14px; line-height: 1.55; }
.gs-root .upload-controls { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; margin-bottom: 14px; }
.gs-root .upload-controls input[type=file] { font-size: 12px; }
.gs-root .upload-status { font-size: 12px; color: var(--text-secondary); }
.gs-root .upload-file-list { margin-top: 8px; }
.gs-root .upload-file-list-title { font-size: 11.5px; text-transform: uppercase; letter-spacing: 0.04em; color: var(--text-muted); font-weight: 650; margin-bottom: 6px; }
.gs-root .upload-file-table { width: 100%; }
.gs-root .upload-file-table thead th { padding: 6px 8px; }
.gs-root .upload-file-table tbody td { font-size: 12px; padding: 6px 8px; }
.gs-root .upload-empty { font-size: 12px; color: var(--text-muted); font-style: italic; }
.gs-root .upload-del-btn { font-size: 11px; color: var(--critical-text); background: none; border: 1px solid var(--critical-text); border-radius: 4px; padding: 3px 8px; cursor: pointer; font-family: inherit; }
.gs-root .upload-del-btn:hover:not(:disabled) { background: var(--critical-text); color: #fff; }
.gs-root .upload-del-btn:disabled { opacity: 0.5; cursor: not-allowed; }
`;

export default KaplStorePage;
